import React, { useState, useEffect, useMemo } from 'react';
import { API_BASE_URL } from '../config';
import {
  Container, Card, CardContent, Button, Typography, Box, Grid,
  CircularProgress, Alert, Accordion, AccordionSummary, AccordionDetails,
  Chip, Divider, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Paper, FormControl, InputLabel, Select, Checkbox, FormControlLabel
} from '@mui/material';
import { ExpandMore, CheckCircle, Cancel, Edit, Warning, Refresh } from '@mui/icons-material';
import axios from 'axios';
import Cookies from 'js-cookie';

const API_BASE = API_BASE_URL;

// Helper to normalize degree names for Firebase keys (no periods/parentheses, keep spaces)
const normalizeDegreeKey = (degreeName) => {
  return degreeName
    .replace(/\./g, '')
    .replace(/\(/g, '')
    .replace(/\)/g, '')
    .replace(/\s+/g, ' ')
    .trim();
};

const AdminAddRequestsPage = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [processing, setProcessing] = useState(null);

  // Batch selection state
  const [selectedRequests, setSelectedRequests] = useState([]);
  const [batchProcessing, setBatchProcessing] = useState(false);
  const [confirmBatchDialogOpen, setConfirmBatchDialogOpen] = useState(false);

  // Year filter state
  const [yearFilter, setYearFilter] = useState('All');
  const [availableYears, setAvailableYears] = useState([]);
  // Sort by registration date: 'oldest' (registered first at top) or 'newest'
  const [sortOrder, setSortOrder] = useState('oldest');

  // Form options
  const [formOptions, setFormOptions] = useState({
    schools: {},
    courseLevels: [],
    honors: [],
    countries: []
  });

  // Edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editedData, setEditedData] = useState({});
  const [selectedSchool, setSelectedSchool] = useState('');
  const [selectedDegree, setSelectedDegree] = useState('');
  const [availableEmphases, setAvailableEmphases] = useState([]);
  const [selectedEmphasis, setSelectedEmphasis] = useState('');
  const [otherEmphasisText, setOtherEmphasisText] = useState('');

  const GENDERS = ["Male", "Female"];

  useEffect(() => {
    fetchRequests();
    fetchFormOptions();
  }, []);

  // Populate dropdowns when editing
  useEffect(() => {
    if (editModalOpen && editingIndex !== null && formOptions.schools) {
      populateEditDropdowns();
    }
  }, [editModalOpen, editingIndex, formOptions]);

  const populateEditDropdowns = () => {
    const request = requests[editingIndex];
    if (!request) return;

    const school = request['School'] || '';
    const degree = request['Degree Obtained'] || '';
    const emphasis = request['Emphasis'] || '';

    console.log('[POPULATE] Full request data:', request);
    console.log('[POPULATE] School:', school);
    console.log('[POPULATE] Degree:', degree);
    console.log('[POPULATE] Emphasis:', emphasis);
    console.log('[POPULATE] Available schools:', Object.keys(formOptions.schools || {}));

    // Check if the school exists in our options
    if (school && formOptions.schools[school]) {
      console.log('[POPULATE] School found! Degrees available:', formOptions.schools[school].degrees);
    } else {
      console.log('[POPULATE] School NOT found in options!');
    }

    setSelectedSchool(school);
    setSelectedDegree(degree);
    setSelectedEmphasis(emphasis);

    // Populate emphasis dropdown if we have school and degree
    if (school && degree && formOptions.schools[school]?.emphases) {
      // Use the degree label directly (not normalized)
      console.log('[POPULATE] Looking for emphases with degree label:', degree);
      const emphases = formOptions.schools[school].emphases[degree] || [];
      console.log('[POPULATE] Available emphases:', emphases);
      setAvailableEmphases(emphases);
    } else {
      console.log('[POPULATE] No emphases available - school:', !!school, 'degree:', !!degree, 'emphases object:', !!formOptions.schools[school]?.emphases);
      setAvailableEmphases([]);
    }
  };

  const normalizeYear = (val) => String(val ?? '').trim().split('.')[0];

  // Parse the AddHold "Application Date" ('YYYY-MM-DD HH:MM:SS'); returns ms or null
  const parseAppDate = (val) => {
    if (!val) return null;
    const t = Date.parse(String(val).replace(' ', 'T'));
    return Number.isNaN(t) ? null : t;
  };

  // "registered X days ago" label from the application date
  const registeredAgo = (val) => {
    const t = parseAppDate(val);
    if (t === null) return null;
    const days = Math.floor((Date.now() - t) / 86400000);
    if (days <= 0) return 'registered today';
    if (days === 1) return 'registered 1 day ago';
    return `registered ${days} days ago`;
  };

  // Derived directly from requests — never stale after a fetch.
  // Carry each request's REAL index in the original array so keys/selection are
  // unique. Then sort by registration date (oldest first by default) so the
  // longest-waiting applicants are handled first.
  const filteredRequests = useMemo(() => {
    let indexed = requests.map((request, actualIndex) => ({ request, actualIndex }));
    if (yearFilter !== 'All') {
      indexed = indexed.filter(({ request }) =>
        normalizeYear(request.graduation) === normalizeYear(yearFilter)
      );
    }
    // Rows without a parseable date sort to the bottom regardless of order
    const dir = sortOrder === 'newest' ? -1 : 1;
    return [...indexed].sort((a, b) => {
      const ta = parseAppDate(a.request['Application Date']);
      const tb = parseAppDate(b.request['Application Date']);
      if (ta === null && tb === null) return 0;
      if (ta === null) return 1;
      if (tb === null) return -1;
      return (ta - tb) * dir;
    });
  }, [requests, yearFilter, sortOrder]);

  const handleClearFilters = () => {
    setYearFilter('All');
    setSortOrder('oldest');
  };

  // Batch selection handlers
  const handleSelectRequest = (index) => {
    setSelectedRequests(prev =>
      prev.includes(index)
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  const handleSelectAll = () => {
    if (selectedRequests.length === filteredRequests.length && filteredRequests.length > 0) {
      setSelectedRequests([]);
    } else {
      // Real indices are already carried on each filtered item
      setSelectedRequests(filteredRequests.map(item => item.actualIndex));
    }
  };

  const handleBatchApprove = () => {
    if (selectedRequests.length === 0) {
      setError('No requests selected');
      return;
    }
    setConfirmBatchDialogOpen(true);
  };

  const confirmBatchApproval = async () => {
    try {
      setBatchProcessing(true);
      setError('');
      setConfirmBatchDialogOpen(false);

      const token = Cookies.get('idToken');
      let approved = 0;
      let failed = 0;

      // Process highest indices first so each deletion doesn't shift lower indices
      const sortedIndices = [...selectedRequests].sort((a, b) => b - a);

      for (const index of sortedIndices) {
        try {
          // Degree formatting and duplicate overwrite are handled by the backend.
          // We skip updateAddHoldEntry here to avoid the per-cell Sheets API calls
          // that cause rate-limit failures when approving many records at once.
          await axios.post(
            `${API_BASE}/admin/add-requests/approve/${index}`,
            { forceApprove: true },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          approved++;
        } catch (err) {
          console.error(`[BATCH] Failed to approve index ${index}:`, err);
          failed++;
        }
      }

      setSuccess(
        `✅ Successfully approved ${approved} request(s)!` +
        (failed > 0 ? ` ${failed} failed.` : '')
      );
      setSelectedRequests([]);
      await fetchRequests();

    } catch (err) {
      console.error('Batch approval error:', err);
      setError(err.response?.data?.error || 'Batch approval failed');
    } finally {
      setBatchProcessing(false);
    }
  };

  const fetchFormOptions = async () => {
    try {
      const token = Cookies.get('idToken');
      const response = await axios.get(`${API_BASE}/admin/form-options`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      console.log('[FORM OPTIONS] Response:', response.data);

      // The response has programsByEra structure, we need to extract schools/degrees
      const allSchools = {};
      const allCourseLevels = new Set();

      if (response.data.programsByEra) {
        // Iterate through all eras to collect schools and degrees
        Object.keys(response.data.programsByEra).forEach(era => {
          const eraData = response.data.programsByEra[era];

          // Handle faculties or schools
          const units = eraData.faculties || eraData.schools || {};

          Object.keys(units).forEach(unitName => {
            const unit = units[unitName];

            // If this school doesn't exist yet, create it
            if (!allSchools[unitName]) {
              allSchools[unitName] = {
                degrees: [],
                degreeLabels: {}, // Map keys to labels
                emphases: {}
              };
            }

            // Add degrees from this school
            if (unit.degrees) {
              Object.keys(unit.degrees).forEach(degreeKey => {
                const degreeData = unit.degrees[degreeKey];
                const degreeLabel = degreeData.label || degreeKey;

                // Store the label for display
                if (!allSchools[unitName].degrees.includes(degreeLabel)) {
                  allSchools[unitName].degrees.push(degreeLabel);
                }

                // Map label back to key for lookups
                allSchools[unitName].degreeLabels[degreeLabel] = degreeKey;

                // Add emphases for this degree (using the label as key)
                if (degreeData.emphases) {
                  if (!allSchools[unitName].emphases[degreeLabel]) {
                    allSchools[unitName].emphases[degreeLabel] = [];
                  }

                  Object.keys(degreeData.emphases).forEach(emphasisKey => {
                    if (!allSchools[unitName].emphases[degreeLabel].includes(emphasisKey)) {
                      allSchools[unitName].emphases[degreeLabel].push(emphasisKey);
                    }
                  });
                }
              });
            }
          });
        });
      }

      console.log('[FORM OPTIONS] Processed schools:', allSchools);

      setFormOptions({
        schools: allSchools,
        courseLevels: ['Certificate', 'Diploma', 'Associate Degree', 'Bachelor Degree', 'Undergraduate', 'Graduate'],
        honors: ['', 'Cum Laude', 'Magna Cum Laude', 'Summa Cum Laude'],
        countries: response.data.countries || ['Trinidad and Tobago', 'United States', 'Canada', 'United Kingdom']
      });
    } catch (err) {
      console.error('Error fetching form options:', err);
    }
  };

  const fetchRequests = async () => {
    setLoading(true);
    setError('');

    try {
      const token = Cookies.get('idToken');
      const response = await axios.get(`${API_BASE}/admin/add-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const requestsList = response.data.requests || [];
      setRequests(requestsList);

      // Extract unique years for filter — normalize to plain 4-digit strings
      const years = [...new Set(
        requestsList
          .map(req => req.graduation ? String(req.graduation).trim().split('.')[0] : null)
          .filter(year => year && /^\d{4}$/.test(year))
      )].sort((a, b) => parseInt(b) - parseInt(a));

      setAvailableYears(years);

    } catch (err) {
      console.error('Error fetching requests:', err);
      setError(err.response?.data?.error || 'Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  // Edit modal handlers
  const handleOpenEdit = (index) => {
    const request = requests[index];
    setEditingIndex(index);
    setEditedData({ ...request });
    setOtherEmphasisText('');
    setEditModalOpen(true);
  };

  const handleCloseEdit = () => {
    setEditModalOpen(false);
    setEditingIndex(null);
    setEditedData({});
    setSelectedSchool('');
    setSelectedDegree('');
    setSelectedEmphasis('');
    setAvailableEmphases([]);
    setOtherEmphasisText('');
  };

  const handleEditChange = (field) => (event) => {
    setEditedData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleSchoolChange = (school) => {
    setSelectedSchool(school);
    setSelectedDegree('');
    setSelectedEmphasis('');
    setAvailableEmphases([]);
    setOtherEmphasisText('');
    setEditedData({
      ...editedData,
      School: school,
      'Degree Obtained': '',
      'Emphasis': ''
    });
  };

  const handleDegreeChange = (degree) => {
    setSelectedDegree(degree);
    setSelectedEmphasis('');
    setOtherEmphasisText('');

    if (degree === 'Other') {
      // When "Other" is selected, clear available emphases and set degree to empty
      setAvailableEmphases([]);
      setEditedData({
        ...editedData,
        'Degree Obtained': '',
        'Emphasis': ''
      });
    } else {
      // Use the degree label directly for emphasis lookup
      const emphases = formOptions.schools[selectedSchool]?.emphases?.[degree] || [];
      setAvailableEmphases(emphases);

      setEditedData({
        ...editedData,
        'Degree Obtained': degree,
        'Emphasis': ''
      });
    }
  };

  const handleEmphasisChange = (emphasis) => {
    setSelectedEmphasis(emphasis);

    if (emphasis === 'Other') {
      setOtherEmphasisText('');
      setEditedData({
        ...editedData,
        'Emphasis': ''
      });
    } else {
      setOtherEmphasisText('');
      setEditedData({
        ...editedData,
        'Emphasis': emphasis
      });
    }
  };

  const handleOtherEmphasisChange = (text) => {
    setOtherEmphasisText(text);
    setEditedData({
      ...editedData,
      'Emphasis': text.trim()
    });
  };

  const updateAddHoldEntry = async (index, data) => {
    const token = Cookies.get('idToken');
    const response = await axios.put(
      `${API_BASE}/admin/add-requests/update/${index}`,
      data,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  };

  const handleSaveAndApprove = async (forceApprove = false) => {
    const message = forceApprove
      ? 'Save changes and approve as separate entry (ignoring duplicates)?'
      : 'Save changes and approve this request?';

    if (!window.confirm(message)) return;

    setProcessing(editingIndex);
    setError('');

    try {
      // Use editedData which has been populated when modal opened
      const dataToApprove = { ...editedData };

      // Determine era from graduation year
      const year = parseInt(dataToApprove.graduation);
      const isPre1985 = year < 1985;

      // Get the actual degree and emphasis values
      let degreeValue = dataToApprove['Degree Obtained'] || '';
      let emphasisValue = dataToApprove['Emphasis'] || '';

      console.log('[SAVE & APPROVE] Original degree:', degreeValue);
      console.log('[SAVE & APPROVE] Original emphasis:', emphasisValue);
      console.log('[SAVE & APPROVE] Era:', isPre1985 ? 'pre-1985' : 'post-1985');

      // Format the degree based on era BEFORE approval
      let formattedDegree = degreeValue;

      // PRE-1985 ONLY: Add course level prefix
      if (isPre1985 && dataToApprove['Course Level'] && degreeValue) {
        formattedDegree = `${dataToApprove['Course Level']} in ${degreeValue}`;
      }

      // Add emphasis if present (all eras)
      if (emphasisValue && emphasisValue !== 'None' && emphasisValue.trim() !== '') {
        formattedDegree += `, Emphasis in ${emphasisValue}`;
      }

      // Update the data with formatted degree
      const finalData = {
        ...dataToApprove,
        'Degree Obtained': formattedDegree
      };

      // Remove the separate emphasis field since it's now incorporated
      delete finalData.Emphasis;

      console.log('[SAVE & APPROVE] Formatted degree:', formattedDegree);
      console.log('[SAVE & APPROVE] Final data:', finalData);

      await updateAddHoldEntry(editingIndex, finalData);
      handleCloseEdit();

      const token = Cookies.get('idToken');
      await axios.post(
        `${API_BASE}/admin/add-requests/approve/${editingIndex}`,
        { forceApprove },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      await fetchRequests();
      setSuccess('✅ Changes saved and request approved!');
    } catch (err) {
      console.error('Error in save and approve:', err);
      setError(err.response?.data?.error || 'Failed to save and approve');
    } finally {
      setProcessing(null);
    }
  };

  // Individual approval/deny handlers
  const handleApprove = async (index, forceApprove = false) => {
    const message = forceApprove
      ? 'Approve this request as a separate entry (ignoring duplicate warning)?'
      : 'Are you sure you want to approve this request?';

    if (!window.confirm(message)) return;

    setProcessing(index);
    setError('');

    try {
      const token = Cookies.get('idToken');
      const request = requests[index];

      console.log('[APPROVE] Original request:', request);

      // Determine era from graduation year
      const year = parseInt(request.graduation);
      const isPre1985 = year < 1985;

      // Get the actual degree and emphasis values
      let degreeValue = request['Degree Obtained'] || '';
      let emphasisValue = request['Emphasis'] || '';

      console.log('[APPROVE] Original degree:', degreeValue);
      console.log('[APPROVE] Original emphasis:', emphasisValue);
      console.log('[APPROVE] Course Level:', request['Course Level']);
      console.log('[APPROVE] Era:', isPre1985 ? 'pre-1985' : 'post-1985');

      // Format the degree based on era BEFORE approval
      let formattedDegree = degreeValue;

      // PRE-1985 ONLY: Add course level prefix
      if (isPre1985 && request['Course Level'] && degreeValue) {
        formattedDegree = `${request['Course Level']} in ${degreeValue}`;
        console.log('[APPROVE] Applied pre-1985 formatting');
      }

      // Add emphasis if present (all eras)
      if (emphasisValue && emphasisValue !== 'None' && emphasisValue.trim() !== '') {
        formattedDegree += `, Emphasis in ${emphasisValue}`;
        console.log('[APPROVE] Added emphasis');
      }

      console.log('[APPROVE] Final formatted degree:', formattedDegree);

      // Update the request with formatted degree
      const dataToApprove = {
        ...request,
        'Degree Obtained': formattedDegree
      };

      // Remove the separate emphasis field
      delete dataToApprove.Emphasis;

      console.log('[APPROVE] Data being sent to update:', dataToApprove);

      // Update the request first
      await updateAddHoldEntry(index, dataToApprove);

      console.log('[APPROVE] Update successful, now approving...');

      // Then approve it
      await axios.post(
        `${API_BASE}/admin/add-requests/approve/${index}`,
        { forceApprove },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      console.log('[APPROVE] Approval successful!');

      await fetchRequests();
      setSuccess('✅ Request approved successfully!');
    } catch (err) {
      console.error('[APPROVE] Error approving request:', err);
      console.error('[APPROVE] Error response:', err.response?.data);
      console.error('[APPROVE] Error status:', err.response?.status);
      setError(err.response?.data?.error || 'Failed to approve request');
    } finally {
      setProcessing(null);
    }
  };

  const handleDeny = async (index) => {
    if (!window.confirm('Are you sure you want to deny this request?')) return;

    setProcessing(index);
    setError('');

    try {
      const token = Cookies.get('idToken');
      await axios.post(
        `${API_BASE}/admin/add-requests/deny/${index}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      await fetchRequests();
      setSuccess('✅ Request denied successfully!');
    } catch (err) {
      console.error('Error denying request:', err);
      setError(err.response?.data?.error || 'Failed to deny request');
    } finally {
      setProcessing(null);
    }
  };

  if (loading) {
    return (
      <Container sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ mt: 2 }}>Loading requests...</Typography>
      </Container>
    );
  }

  const degreeOptions = selectedSchool && formOptions.schools[selectedSchool]
    ? formOptions.schools[selectedSchool].degrees || []
    : [];

  console.log('[RENDER] selectedSchool:', selectedSchool);
  console.log('[RENDER] degreeOptions:', degreeOptions);
  console.log('[RENDER] selectedDegree:', selectedDegree);
  console.log('[RENDER] selectedEmphasis:', selectedEmphasis);
  console.log('[RENDER] availableEmphases:', availableEmphases);

  const currentRequest = editingIndex !== null ? requests[editingIndex] : null;
  const hasDuplicates = currentRequest?.hasDuplicate && currentRequest?.duplicates?.length > 0;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Alumni Registration Requests</Typography>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={fetchRequests}
        >
          Refresh
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" paragraph>
        Review and approve/deny pending alumni registration requests
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Filter Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Filter Requests
          </Typography>

          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Graduation Year</InputLabel>
                <Select
                  value={yearFilter}
                  onChange={(e) => setYearFilter(e.target.value)}
                  label="Graduation Year"
                >
                  <MenuItem value="All">All Years</MenuItem>
                  {availableYears.map((year) => (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                  label="Sort By"
                >
                  <MenuItem value="oldest">Registered first (oldest)</MenuItem>
                  <MenuItem value="newest">Registered last (newest)</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={3}>
              <Button
                variant="outlined"
                onClick={handleClearFilters}
                disabled={yearFilter === 'All' && sortOrder === 'oldest'}
              >
                Clear Filters
              </Button>
            </Grid>

            <Grid item xs={12} sm={3}>
              <Typography variant="body2" color="text.secondary">
                Showing {filteredRequests.length} of {requests.length} requests
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Batch Selection Controls */}
      {filteredRequests.length > 0 && (
        <Card sx={{ mb: 3, bgcolor: '#f5f5f5' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedRequests.length === filteredRequests.length && filteredRequests.length > 0}
                    indeterminate={selectedRequests.length > 0 && selectedRequests.length < filteredRequests.length}
                    onChange={handleSelectAll}
                    sx={{
                      color: '#046A38',
                      '&.Mui-checked': { color: '#046A38' }
                    }}
                  />
                }
                label={`Select All (${filteredRequests.length})`}
              />

              <Button
                variant="contained"
                disabled={selectedRequests.length === 0 || batchProcessing}
                onClick={handleBatchApprove}
                sx={{
                  bgcolor: '#046A38',
                  '&:hover': { bgcolor: '#035229' },
                  '&:disabled': { bgcolor: '#cccccc' }
                }}
              >
                {batchProcessing ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                    Processing...
                  </>
                ) : (
                  `Approve Selected (${selectedRequests.length})`
                )}
              </Button>

              {selectedRequests.length > 0 && (
                <Typography variant="body2" color="text.secondary">
                  {selectedRequests.length} request{selectedRequests.length !== 1 ? 's' : ''} selected
                </Typography>
              )}
            </Box>
          </CardContent>
        </Card>
      )}

      {filteredRequests.length === 0 ? (
        <Alert severity="info">
          {requests.length === 0
            ? 'No pending requests at this time.'
            : 'No requests match your filters'}
        </Alert>
      ) : (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Total Requests: {filteredRequests.length}
          </Typography>

          {/* Request Accordions */}
          {filteredRequests.map(({ request, actualIndex }) => {
            return (
              <Accordion
                key={actualIndex}
                sx={{
                  mb: 2,
                  border: selectedRequests.includes(actualIndex) ? '2px solid #046A38' : '1px solid #e0e0e0',
                  bgcolor: selectedRequests.includes(actualIndex) ? '#f0f8f5' : 'white'
                }}
              >
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                    <Checkbox
                      checked={selectedRequests.includes(actualIndex)}
                      onChange={() => handleSelectRequest(actualIndex)}
                      onClick={(e) => e.stopPropagation()}
                      sx={{
                        color: '#046A38',
                        '&.Mui-checked': { color: '#046A38' }
                      }}
                    />
                    <Typography variant="h6">
                      {request['FULL NAME'] ||
                       `${request.First_Name || ''} ${request.Last_Name || ''}`.trim() ||
                       'Unknown Name'}
                    </Typography>
                    {request.hasDuplicate && (
                      <Chip
                        label="⚠️ Potential Duplicate"
                        color="warning"
                        size="small"
                      />
                    )}
                    {request.hasDuplicate && (
                      <Chip
                        label={request.duplicateRegistered
                          ? 'Already registered (has account)'
                          : 'Duplicate not yet registered'}
                        color={request.duplicateRegistered ? 'error' : 'default'}
                        size="small"
                        variant={request.duplicateRegistered ? 'filled' : 'outlined'}
                      />
                    )}
                    <Chip
                      label={`Year: ${request.graduation}`}
                      size="small"
                      variant="outlined"
                    />
                    {registeredAgo(request['Application Date']) && (
                      <Chip
                        label={registeredAgo(request['Application Date'])}
                        size="small"
                        color="info"
                        variant="outlined"
                      />
                    )}
                  </Box>
                </AccordionSummary>

                <AccordionDetails>
                  {/* Always show submission details */}
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ bgcolor: 'primary.light', color: 'primary.contrastText', p: 1, borderRadius: 1, mb: 2 }}>
                      <Typography variant="subtitle1" fontWeight="bold">✨ Submission Details</Typography>
                    </Box>
                    <DisplayFields data={request} />
                  </Box>

                  {/* Duplicate Warning — uses existingRecord returned by backend */}
                  {request.hasDuplicate && request.existingRecord && (
                    <Box sx={{ mb: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, p: 2, bgcolor: '#fff3e0', borderRadius: 1 }}>
                        <Warning color="warning" sx={{ mr: 1 }} />
                        <Typography variant="subtitle1" color="warning.main" fontWeight="bold">
                          Potential Duplicate Detected in MasterList
                        </Typography>
                      </Box>

                      <Alert
                        severity={request.duplicateRegistered ? 'error' : 'info'}
                        sx={{ mb: 2 }}
                      >
                        {request.duplicateRegistered
                          ? 'This person is already registered (their existing record has an Alumni ID / account). Approving will replace that existing record.'
                          : 'A matching record exists but is not yet registered (no Alumni ID). Approving will replace it and create their account.'}
                      </Alert>

                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Paper sx={{ p: 2, bgcolor: '#e8f5e9', border: '2px solid #4caf50' }}>
                            <Typography variant="subtitle1" fontWeight="bold" color="success.dark" sx={{ mb: 2 }}>
                              ✨ New Submission
                            </Typography>
                            <DisplayFields data={request} compact />
                          </Paper>
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <Paper sx={{ p: 2, bgcolor: '#f5f5f5', border: '2px solid #9e9e9e' }}>
                            <Typography variant="subtitle1" fontWeight="bold" color="text.secondary" sx={{ mb: 2 }}>
                              📋 Existing Record
                            </Typography>
                            <DisplayFields data={request.existingRecord} compact />
                          </Paper>
                        </Grid>
                      </Grid>

                      <Box sx={{ mt: 2, p: 2, bgcolor: '#fff3e0', borderRadius: 1 }}>
                        <Button
                          variant="contained"
                          color="warning"
                          startIcon={<CheckCircle />}
                          onClick={() => handleApprove(actualIndex, true)}
                          disabled={processing === actualIndex}
                        >
                          Approve as Separate Entry
                        </Button>
                        <Typography variant="caption" sx={{ ml: 2, color: 'text.secondary' }}>
                          Use this if the detection is incorrect and this is a different person
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  <Divider sx={{ my: 3 }} />

                  {/* Action Buttons */}
                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                    <Button
                      variant="outlined"
                      startIcon={<Edit />}
                      onClick={() => handleOpenEdit(actualIndex)}
                      disabled={processing === actualIndex}
                    >
                      Edit Before Approval
                    </Button>

                    <Button
                      variant="contained"
                      color="error"
                      startIcon={<Cancel />}
                      onClick={() => handleDeny(actualIndex)}
                      disabled={processing === actualIndex}
                    >
                      {processing === actualIndex ? 'Processing...' : 'Deny'}
                    </Button>

                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<CheckCircle />}
                      onClick={() => handleApprove(actualIndex, false)}
                      disabled={processing === actualIndex}
                    >
                      {processing === actualIndex ? 'Processing...' : 'Approve'}
                    </Button>
                  </Box>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      )}

      {/* Batch Approval Confirmation Dialog */}
      <Dialog
        open={confirmBatchDialogOpen}
        onClose={() => setConfirmBatchDialogOpen(false)}
      >
        <DialogTitle sx={{ color: '#046A38' }}>
          Confirm Batch Approval
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to approve <strong>{selectedRequests.length}</strong> request(s)?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            This will:
          </Typography>
          <ul>
            <li>
              <Typography variant="body2">Create Firebase accounts for users with valid emails</Typography>
            </li>
            <li>
              <Typography variant="body2">Add approved records to the MasterList</Typography>
            </li>
            <li>
              <Typography variant="body2">
                <strong>Automatically overwrite</strong> any existing MasterList record that matches the same name and graduation year
              </Typography>
            </li>
            <li>
              <Typography variant="body2">Remove these requests from the pending queue</Typography>
            </li>
          </ul>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmBatchDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={confirmBatchApproval}
            variant="contained"
            sx={{
              bgcolor: '#046A38',
              '&:hover': { bgcolor: '#035229' }
            }}
          >
            Confirm Approval
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={editModalOpen} onClose={handleCloseEdit} maxWidth="md" fullWidth>
        <DialogTitle>Edit Submission Before Approval</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              {/* Personal Information */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" fontWeight="bold" color="primary" sx={{ mb: 1 }}>
                  Personal Information
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  value={editedData.First_Name || ''}
                  onChange={handleEditChange('First_Name')}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  value={editedData.Last_Name || ''}
                  onChange={handleEditChange('Last_Name')}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Gender"
                  value={editedData.Gender || ''}
                  onChange={handleEditChange('Gender')}
                >
                  {GENDERS.map((gender) => (
                    <MenuItem key={gender} value={gender}>{gender}</MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Student ID"
                  value={editedData['Student Id'] || ''}
                  onChange={handleEditChange('Student Id')}
                />
              </Grid>

              {/* Academic Information */}
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold" color="primary" sx={{ mb: 1 }}>
                  Academic Information
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="School"
                  value={selectedSchool || ''}
                  onChange={(e) => handleSchoolChange(e.target.value)}
                >
                  {Object.keys(formOptions.schools || {}).map((school) => (
                    <MenuItem key={school} value={school}>{school}</MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Qualification"
                  value={selectedDegree || ''}
                  onChange={(e) => handleDegreeChange(e.target.value)}
                  disabled={!selectedSchool}
                >
                  {degreeOptions.map((degree) => (
                    <MenuItem key={degree} value={degree}>{degree}</MenuItem>
                  ))}
                  <MenuItem value="Other">Other</MenuItem>
                </TextField>
              </Grid>

              {/* Show text field when "Other" degree is selected */}
              {selectedDegree === 'Other' && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Specify Other Qualification"
                    value={editedData['Degree Obtained'] === 'Other' ? '' : editedData['Degree Obtained'] || ''}
                    onChange={(e) => {
                      const customDegree = e.target.value;
                      setEditedData({
                        ...editedData,
                        'Degree Obtained': customDegree
                      });
                    }}
                    placeholder="Enter custom qualification"
                  />
                </Grid>
              )}

              {availableEmphases.length > 0 && selectedDegree !== 'Other' && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    select
                    label="Emphasis"
                    value={selectedEmphasis || ''}
                    onChange={(e) => handleEmphasisChange(e.target.value)}
                  >
                    <MenuItem value="">None</MenuItem>
                    {availableEmphases.map((emphasis) => (
                      <MenuItem key={emphasis} value={emphasis}>{emphasis}</MenuItem>
                    ))}
                    <MenuItem value="Other">Other</MenuItem>
                  </TextField>
                </Grid>
              )}

              {selectedEmphasis === 'Other' && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Specify Other Emphasis"
                    value={otherEmphasisText}
                    onChange={(e) => handleOtherEmphasisChange(e.target.value)}
                    placeholder="Enter custom emphasis"
                  />
                </Grid>
              )}

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Course Level"
                  value={editedData['Course Level'] || ''}
                  onChange={handleEditChange('Course Level')}
                >
                  <MenuItem value="Certificate">Certificate</MenuItem>
                  <MenuItem value="Diploma">Diploma</MenuItem>
                  <MenuItem value="Associate Degree">Associate Degree</MenuItem>
                  <MenuItem value="Bachelor Degree">Bachelor Degree</MenuItem>
                  <MenuItem value="Undergraduate">Undergraduate</MenuItem>
                  <MenuItem value="Graduate">Graduate</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Graduation Year"
                  value={editedData.graduation || ''}
                  onChange={handleEditChange('graduation')}
                >
                  {(() => {
                    const currentYear = new Date().getFullYear();
                    const years = Array.from({ length: currentYear - 1926 }, (_, i) => currentYear - i);
                    return years.map((year) => (
                      <MenuItem key={year} value={year}>
                        {year}
                      </MenuItem>
                    ));
                  })()}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Honors"
                  value={editedData.Honors || ''}
                  onChange={handleEditChange('Honors')}
                >
                  {formOptions.honors.map((honor) => (
                    <MenuItem key={honor} value={honor}>{honor || 'None'}</MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* Contact Information */}
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold" color="primary" sx={{ mb: 1 }}>
                  Contact Information
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email Contact"
                  type="email"
                  value={editedData['Email Contact'] || ''}
                  onChange={handleEditChange('Email Contact')}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Contact Number"
                  value={editedData['Contact Number'] || ''}
                  onChange={handleEditChange('Contact Number')}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Country"
                  value={editedData.Country || ''}
                  onChange={handleEditChange('Country')}
                >
                  {formOptions.countries.map((country) => (
                    <MenuItem key={country} value={country}>{country}</MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  value={editedData.Address || ''}
                  onChange={handleEditChange('Address')}
                  multiline
                  rows={2}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="USC Email"
                  value={editedData['USC Email'] || ''}
                  onChange={handleEditChange('USC Email')}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Alternative Phone Number"
                  value={editedData['Alternative Phone Number'] || ''}
                  onChange={handleEditChange('Alternative Phone Number')}
                />
              </Grid>

              {/* Additional Information */}
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold" color="primary" sx={{ mb: 1 }}>
                  Additional Information
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Other Degree Attained"
                  value={editedData['Other Degree Attained'] || ''}
                  onChange={handleEditChange('Other Degree Attained')}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Current Job Title"
                  value={editedData['Current Job Title'] || ''}
                  onChange={handleEditChange('Current Job Title')}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Attending (current institution)"
                  value={editedData.Attending || ''}
                  onChange={handleEditChange('Attending')}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Referred By"
                  value={editedData['Referred By'] || ''}
                  onChange={handleEditChange('Referred By')}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Share With Employers"
                  value={editedData['Share With Employers'] || 'No'}
                  onChange={handleEditChange('Share With Employers')}
                >
                  <MenuItem value="No">No — Keep profile private</MenuItem>
                  <MenuItem value="Yes">Yes — Share with employers</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEdit}>Cancel</Button>

          {hasDuplicates && (
            <Button
              variant="outlined"
              color="warning"
              onClick={() => handleSaveAndApprove(true)}
              disabled={processing === editingIndex}
            >
              Save & Approve as Separate
            </Button>
          )}

          <Button
            variant="contained"
            onClick={() => handleSaveAndApprove(false)}
            startIcon={<CheckCircle />}
            disabled={processing === editingIndex}
          >
            {processing === editingIndex ? 'Processing...' : 'Save & Approve'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

// Helper component to display fields
const DisplayFields = ({ data, compact = false }) => {
  if (!data) {
    return <Alert severity="warning">No data available</Alert>;
  }

  const excludeFields = ['Password', 'PendingPassword', 'password', 'hasDuplicate', 'existingRecord', 'duplicates', 'index', 'confidence', 'reason'];

  const importantFields = [
    'FULL NAME', 'First_Name', 'Last_Name', 'Email Contact', 'Contact Number',
    'School', 'Degree Obtained', 'Emphasis', 'graduation', 'Course Level', 'Honors', 'Student Id',
    'Gender', 'Country', 'Address', 'Referred By', 'Application Date'
  ];

  const formatValue = (value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'object') {
      try {
        return JSON.stringify(value, null, 2);
      } catch (e) {
        return '[Object]';
      }
    }
    if (Array.isArray(value)) return value.join(', ');
    return String(value);
  };

  const allFields = Object.keys(data);
  const sortedFields = [
    ...importantFields.filter(f => allFields.includes(f)),
    ...allFields.filter(f => !importantFields.includes(f) && !excludeFields.includes(f)).sort()
  ];

  return (
    <Box>
      {sortedFields.map((key) => {
        const rawValue = data[key];
        const value = formatValue(rawValue);

        if (excludeFields.includes(key) || !value || value === '' || value === 'null') {
          return null;
        }

        return (
          <Box key={key} sx={{ mb: compact ? 1 : 1.5, pb: compact ? 0.5 : 1, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: compact ? '0.7rem' : '0.75rem' }}>
              {key.replace(/_/g, ' ')}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5, wordBreak: 'break-word', whiteSpace: 'pre-wrap', fontSize: compact ? '0.8rem' : '0.875rem' }}>
              {value}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

export default AdminAddRequestsPage;